import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, LogIn, LayoutDashboard, LogOut, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

const LOGO_URL = "/logo.png";

export default function Navbar({ user, onLogout }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // India-only site — country is always "in"
  const currentCountryCode = "in";

  // Helper to build country-aware paths
  const cp = (path) => `/${currentCountryCode}${path}`;

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === "hi" ? "en" : "hi");
  };

  const serviceLinks = [
    { name: "Business Consultancy", path: cp("/services/business-consultancy") },
    { name: "Business Setup", path: cp("/services/business-setup") },
    { name: "Marketing Strategy", path: cp("/services/marketing-strategy") },
    { name: "Digital Marketing", path: cp("/services/digital-marketing") },
    { name: "Paid Advertising", path: cp("/services/paid-advertising") },
    { name: "SEO Services", path: cp("/services/seo-services") },
    { name: "Website Development", path: cp("/services/website-development") },
    { name: "Branding & Creative", path: cp("/services/branding-creative") },
    { name: "Growth & Optimization", path: cp("/services/growth-optimization") },
    { name: "B2B Solutions", path: cp("/services/b2b-solutions") },
    { name: "Legal & Compliance", path: cp("/services/legal-compliance") },
    { name: "View All Services", path: cp("/services") },
  ];

  const navLinks = [
    { name: t("nav.home"), path: cp("") },
    { name: t("nav.about"), path: cp("/about") },
    { name: t("nav.services"), path: cp("/services"), hasDropdown: true },
    { name: t("nav.industries"), path: cp("/industries") },
    { name: t("nav.cities"), path: cp("/cities") },
    { name: t("nav.pricing"), path: cp("/pricing") },
    { name: t("nav.blog"), path: cp("/blog") },
    { name: t("nav.contact"), path: cp("/contact") },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="main-navbar" className="fixed top-0 left-0 right-0 z-50 bg-white backdrop-blur-xl border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to={cp("")} className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="Brand Ur Business" width="48" height="48" fetchPriority="high" decoding="async" className="h-10 w-10 lg:h-12 lg:w-12 object-contain" />
            <div className="hidden sm:block">
              <span className="text-gray-900 font-bold text-lg tracking-tight" style={{ fontFamily: 'Outfit' }}>Brand Ur Business</span>
              <p className="text-[10px] text-gray-500 -mt-1 tracking-wider uppercase">Business Made Simple</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.name} className="relative group">
                {link.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${
                        isActive(link.path) ? "text-gray-900" : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {link.name}
                      <ChevronDown className="w-3 h-3" />
                    </Link>
                    {servicesOpen && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-xl py-2 shadow-xl">
                        {serviceLinks.map((sl) => (
                          <Link
                            key={sl.path}
                            to={sl.path}
                            className="block px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                          >
                            {sl.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      isActive(link.path) ? "text-gray-900" : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Right section */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Language Toggle */}
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-full transition-all"
              data-testid="lang-toggle-btn"
              title={i18n.language === "hi" ? "Switch to English" : "Switch to Hindi"}
            >
              <Globe className="w-3.5 h-3.5" />
              {i18n.language === "hi" ? "EN" : "HI"}
            </button>
            {user ? (
              <div className="flex items-center gap-2">
                <Link
                  to={user.role === 'admin' ? '/admin' : '/dashboard'}
                  className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-900 text-sm font-medium px-4 py-2 rounded-full transition-all"
                  data-testid="navbar-dashboard-btn"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  {t("nav.dashboard")}
                </Link>
                <button
                  onClick={onLogout}
                  className="p-2 text-gray-500 hover:text-gray-900 transition-colors"
                  data-testid="navbar-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-yellow-400 text-white text-sm font-bold px-5 py-2.5 rounded-full hover:opacity-90 transition-opacity shadow-md"
                data-testid="navbar-login-btn"
              >
                <LogIn className="w-4 h-4" />
                {t("nav.login")}
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 text-gray-600 hover:text-gray-900"
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  isActive(link.path) ? "text-gray-900 bg-gray-100" : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-200 space-y-2">
              <button
                onClick={toggleLang}
                className="flex items-center gap-2 w-full px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
                data-testid="mobile-lang-toggle-btn"
              >
                <Globe className="w-4 h-4" />
                {i18n.language === "hi" ? "Switch to English" : "Switch to Hindi"}
              </button>
              {user ? (
                <>
                  <Link
                    to={user.role === 'admin' ? '/admin' : '/dashboard'}
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-3 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg"
                  >
                    {t("nav.dashboard")}
                  </Link>
                  <button
                    onClick={() => { onLogout(); setMobileOpen(false); }}
                    className="block w-full text-left px-4 py-3 text-sm font-medium text-gray-500 hover:text-gray-900 rounded-lg"
                  >
                    {t("nav.logout")}
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 text-sm font-bold text-white bg-gradient-to-r from-purple-600 to-yellow-400 rounded-lg text-center"
                >
                  {t("nav.login")} / Sign Up
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
